import { createContext } from 'react';
import { RID } from '@sharefiledev/sfplugin-resource-id';
import {
	Locale,
	LocaleKeys,
	PermissionByType,
	PermissionsConfiguration,
	PermissionsConfigurationsByType,
	PresetsByResourceType,
	PresetsByType,
	SelectedResource,
	ShareSuccessReturnData,
} from '@sharefiledev/sharing-pilet-sdk';
import {
	AvailablePermissionSet,
	EvaluatePost200Response,
	Preset,
	Principal,
	RelatedResource,
	SharablePermissionSet,
	SharablePreset,
	SharableRelatedResource,
	User,
} from '@sharefiledev/sharing-pilet-sdk/api';
import { PermissionId } from '../ram-types';
import { PluginTFunction } from '../utils';
import { PermissionsSelectionByType } from './PluginContextProvider';

export interface IConfigurationByResourceType {
	configurationByResourceType?: PermissionsConfigurationsByType;
	availablePermissionSetsByType: PermissionByType;
	presetsByResourceType: PresetsByResourceType;
	selectedPermissionsByType: PermissionsSelectionByType;
	replacePermissionsByType: (permissions: PermissionsSelectionByType) => void;
	sharablePresetsByType: PresetsByType[];
}

export interface IPermissionContext extends IConfigurationByResourceType {
	subjectRIDs: RID[];
	/** @todo decide if this is really the same context */
	assignedUserIds: string[];
	sharablePermissions: SharablePermissionSet[];
	sharablePresets: SharablePreset[];
	sharableRelatedResources: SharableRelatedResource[];
	availablePermissions: AvailablePermissionSet[];
	configuration: PermissionsConfiguration;
	presets: Preset[];
	selectedPermissions: PermissionId[];
	selectedPreset: string | undefined;
	shareEntity: string;
	canShare: boolean;
	canQueryUsers: boolean;
	selectPreset(presetId: string | undefined): void;
	addPermission(permissionIds: PermissionId[]): void;
	removePermission(permissionIds: PermissionId[]): void;
	replacePermission(newPermissionIds: PermissionId[]): void;
	selectedPrincipals: Principal[];
	addUserOrPrincipal(userOrPrincipal: User | Principal): void;
	removeUserOrPrincipal(userOrPrincipal: User | Principal): void;
	replacePrincipals(principals: Principal[]): void;
	disableCustomPermissions?: boolean;
	executeReplace(
		principalRIDs: string[],
		newPermissions: string[],
		relatedResourcesToShare?: RelatedResource[],
		userOrUserGroupRids?: string[],
		resourceRids?: string[],
		permissionExpiryInDays?: number | null
	): Promise<ShareSuccessReturnData>;
	sharableRelatedResourcesStatus: { [key: string]: boolean };
	changeSharableRelatedResourceStatus(rid: string, isChecked: boolean): void;
	setSharableRelatedResourcesStatus: React.Dispatch<
		React.SetStateAction<{ [key: string]: boolean }>
	>;
	clearSelection(): void;
	evaluateResponse?: EvaluatePost200Response;
	allowedUserRole?: string;
	shareButtonText?: string;
	shareButtonTextInManageModal?: string;
	shareModalTitle?: string;
	manageUsersModalTitle?: string;
	removeUserConfirmMessage?: string;
	entityType?: string;
	labels?: Locale<LocaleKeys>[];
	t: PluginTFunction;
	eventId?: string;
	isFetchingUsers?: boolean;
	disableMultiUserSelect?: boolean;
	setSelectedResources: (resources: SelectedResource[]) => void;
	selectedResources: SelectedResource[];
	isAsyncEvaluationLoading: boolean;
	allowShareToUserGroups?: boolean;
	hidePermissionDrawer?: boolean;
	relatedResourcesToShare: SharableRelatedResource[] | undefined;
	setRelatedResourcesToShare: React.Dispatch<
		React.SetStateAction<SharableRelatedResource[] | undefined>
	>;
	skipNotification?: boolean;
}

const throwUnmounted: any = () => {
	throw new Error('Unmounted context');
};

export const defaultPermissionContext: IPermissionContext = {
	subjectRIDs: [],
	configuration: { availablePermissionSets: [], presets: [] },
	assignedUserIds: [],
	sharablePermissions: [],
	sharablePresets: [],
	sharableRelatedResources: [],
	availablePermissions: [],
	presets: [],
	selectedPermissions: [],
	selectedPreset: '',
	shareEntity: '',
	canShare: false,
	canQueryUsers: false,
	selectPreset: throwUnmounted,
	addPermission: throwUnmounted,
	removePermission: throwUnmounted,
	replacePermission: throwUnmounted,
	selectedPrincipals: [],
	addUserOrPrincipal: throwUnmounted,
	removeUserOrPrincipal: throwUnmounted,
	replacePrincipals: throwUnmounted,
	disableCustomPermissions: false,
	executeReplace: throwUnmounted,
	sharableRelatedResourcesStatus: {},
	changeSharableRelatedResourceStatus: throwUnmounted,
	setSharableRelatedResourcesStatus: throwUnmounted,
	clearSelection: throwUnmounted,
	allowedUserRole: '',
	shareButtonText: '',
	shareButtonTextInManageModal: '',
	shareModalTitle: '',
	manageUsersModalTitle: '',
	removeUserConfirmMessage: '',
	entityType: '',
	labels: [],
	t: throwUnmounted,
	isFetchingUsers: false,
	disableMultiUserSelect: false,
	setSelectedResources: throwUnmounted,
	selectedResources: [],
	isAsyncEvaluationLoading: false,
	allowShareToUserGroups: false,
	hidePermissionDrawer: false,
	relatedResourcesToShare: undefined,
	setRelatedResourcesToShare: throwUnmounted,
	configurationByResourceType: {
		availablePermissionSetsByType: {},
		presetsByType: {},
	},
	availablePermissionSetsByType: {},
	presetsByResourceType: {},
	selectedPermissionsByType: {},
	replacePermissionsByType: throwUnmounted,
	sharablePresetsByType: [],
	skipNotification: false,
};

export const PermissionContext = createContext<IPermissionContext>(
	defaultPermissionContext
);
